/**
 * The opening chain.
 *
 * Established browser strategy games open with a guided sequence, and for good
 * reason: a village screen with eleven buildings and no direction is a
 * wall. These quests are not a tutorial in the modal-popup sense. They are a
 * checklist of the things a new holding needs, in the order it needs them,
 * each paying enough to fund the next.
 *
 * Two rules kept throughout:
 *  - Every quest is something the player would want to do anyway. None of them
 *    exist to teach a button.
 *  - Progress is computed from live game state, never incremented by an event.
 *    That means a quest cannot desync, and a player who did the thing before
 *    the quest existed already has it done.
 */
export const QuestGoal = {
    BUILDING_LEVEL: 'building_level',
    TOTAL_BUILDING_LEVELS: 'total_building_levels',
    TRAIN_UNITS: 'train_units',
    ARMY_SIZE: 'army_size',
    VILLAGE_POINTS: 'village_points',
    CLEAR_CAMP: 'clear_camp',
    WIN_BATTLES: 'win_battles',
    JOIN_ALLIANCE: 'join_alliance',
    RESOURCES_HELD: 'resources_held',
};
export const QUESTS = [
    {
        id: 'first-timber',
        title: 'Fell the first timber',
        brief: 'Nothing else gets built until the saws are running. Raise the Timber Camp.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'timber_camp',
        target: 3,
        reward: { wood: 250, clay: 200, iron: 120, grain: 100 },
    },
    {
        id: 'clay-and-iron',
        title: 'Break ground on clay',
        brief: 'Timber alone raises nothing but sheds. Get the pit to level 3.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'clay_pit',
        target: 3,
        reward: { wood: 150, clay: 150, iron: 100, grain: 50 },
    },
    {
        id: 'iron-in-the-hills',
        title: 'Iron in the hills',
        brief: 'Weapons are bought in ore. Get the mine to level 3.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'iron_mine',
        target: 3,
        reward: { wood: 150, clay: 150, iron: 100, grain: 50 },
    },
    {
        id: 'feed-them',
        title: 'Feed the village',
        brief: 'Every soldier eats. Raise the Farm to level 5 before you raise an army.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'farm',
        target: 5,
        // The farm can feed them now, so the first handful of soldiers arrive
        // with the harvest rather than waiting on the barracks.
        reward: { grain: 400, wood: 200, units: { spearman: 6 } },
    },
    {
        id: 'somewhere-to-put-it',
        title: 'Somewhere to put it',
        brief: 'Full stores waste everything they produce. Raise the Warehouse to level 5.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'warehouse',
        target: 5,
        reward: { wood: 300, clay: 300 },
    },
    {
        id: 'seat-of-the-lord',
        title: 'Seat of the lord',
        brief: 'The Manor speeds every other build. Take it to level 5.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'manor_house',
        target: 5,
        reward: { wood: 400, clay: 400, iron: 250 },
    },
    {
        id: 'raise-a-guard',
        title: 'Raise a guard',
        brief: 'An undefended village is an invitation. Put 20 spearmen behind the gate.',
        goal: QuestGoal.TRAIN_UNITS,
        subject: 'spearman',
        target: 20,
        // Veterans answer the muster: half the guard is granted, so the quest
        // costs ten spearmen of resources rather than twenty.
        reward: { wood: 400, clay: 250, iron: 250, grain: 150, units: { spearman: 10 } },
    },
    {
        id: 'first-blood',
        title: 'First blood',
        brief: 'There is a bandit camp within reach. Break it and take what they have taken.',
        goal: QuestGoal.CLEAR_CAMP,
        target: 1,
        // A taste of the offence line before the barracks can train it.
        reward: { wood: 500, clay: 500, iron: 400, grain: 300, units: { axeman: 4 } },
    },
    {
        id: 'a-standing-army',
        title: 'A standing army',
        brief: 'One warband is a raid. Sixty men is a threat. Field sixty of any kind.',
        goal: QuestGoal.ARMY_SIZE,
        target: 60,
        reward: { wood: 600, clay: 600, iron: 500, gold: 50 },
    },
    {
        id: 'stone-on-stone',
        title: 'Stone on stone',
        brief: 'A wall turns a village into a problem. Raise it to level 5.',
        goal: QuestGoal.BUILDING_LEVEL,
        subject: 'wall',
        target: 5,
        reward: { clay: 800, iron: 500 },
    },
    {
        id: 'three-camps',
        title: 'Clear the marches',
        brief: 'Bandits will keep coming back. Break three camps and make the point.',
        goal: QuestGoal.CLEAR_CAMP,
        target: 3,
        reward: { wood: 900, clay: 900, iron: 700, grain: 500 },
    },
    {
        id: 'find-friends',
        title: 'Find friends',
        brief: 'Nobody holds a march alone. Join an alliance, or found one.',
        goal: QuestGoal.JOIN_ALLIANCE,
        target: 1,
        reward: { wood: 500, clay: 500, gold: 100 },
    },
    {
        id: 'a-place-of-note',
        title: 'A place of note',
        brief: 'Build your holding to 1,000 points and it starts showing on the map.',
        goal: QuestGoal.VILLAGE_POINTS,
        target: 1000,
        reward: { wood: 1200, clay: 1200, iron: 900, grain: 600, gold: 150 },
    },
];
export const QUESTS_BY_ID = Object.fromEntries(QUESTS.map((q) => [q.id, q]));
//# sourceMappingURL=quests.js.map